/* src/components/RoomsPage.jsx */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from './ui/Card';
import Button from './ui/Button';
import Modal from './ui/Modal';
import Input from './ui/Input';

const TrashIcon = () => (/* ... svg ... */
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);
const PencilIcon = () => (/* ... svg ... */
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);
const PlusIcon = () => (/* ... svg ... */
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);
function RoomsPage({ rooms: apiRooms, onAddRoom, onUpdateRoom, onDeleteRoom }) {
  const navigate = useNavigate();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');

  // roomToEdit will still hold room_code for display purposes
  const [roomToEdit, setRoomToEdit] = useState({ id: null, name: '', room_code: '' });

  const rooms = apiRooms || [];

  // --- Add Room Modal ---
  const openAddModal = () => {
    setNewRoomName('');
    setIsAddModalOpen(true);
  };
  const closeAddModal = () => setIsAddModalOpen(false);

  const handleAddRoomSubmit = (e) => {
    e.preventDefault();
    const trimmedName = newRoomName.trim();
    if (!trimmedName) {
      alert('Room name is required.');
      return;
    }
    onAddRoom({ name: trimmedName });
    closeAddModal();
  };

  // --- Edit Room Modal ---
  const openEditModal = (room) => {
    setRoomToEdit({ id: room.id, name: room.name, room_code: room.room_code || 'N/A' }); // Display 'N/A' if somehow missing
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setRoomToEdit({ id: null, name: '', room_code: '' });
  };

  const handleEditRoomInputChange = (e) => {
    const { name, value } = e.target;
    // Only update the name, room_code is not editable by user input here
    if (name === 'name') {
      setRoomToEdit(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveEditRoomSubmit = (e) => {
    e.preventDefault();
    const { id, name } = roomToEdit; // Only destructure id and name
    if (!name.trim()) {
      alert('Room name is required.');
      return;
    }
    // Payload for update will only contain fields that can be changed (name)
    const payload = { id, name: name.trim() };
    onUpdateRoom(payload);
    closeEditModal();
  };

  // --- Delete Room ---
  const handleDeleteRoom = (roomIdToDelete, roomName) => {
    if (window.confirm(`Are you sure you want to delete room "${roomName}"? This action cannot be undone.`)) {
      onDeleteRoom(roomIdToDelete);
      if (isEditModalOpen && roomToEdit.id === roomIdToDelete) {
        closeEditModal();
      }
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen">
      {/* ... (Card for Room List and Add button remains the same) ... */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-700">Rooms List</h2>
            <Button
              variant="primary"
              className="text-sm py-1 px-3 flex items-center space-x-1"
              onClick={openAddModal}
            >
              <span className="text-lg font-bold leading-none mr-1">+</span>
              <span>Add Room</span>
            </Button>
          </div>
          <div className="overflow-x-auto">
            <ul className="divide-y divide-gray-200 border rounded-md">
              {rooms.length > 0 ? (
                rooms.map((room) => (
                  <li key={room.id} className="px-4 py-3 flex justify-between items-center hover:bg-gray-50">
                    <div
                      className="cursor-pointer flex-1"
                      onClick={() => navigate(`/setup/rooms/${room.id}`)}
                      title={`View details for ${room.name}`}
                    >
                      <span className="text-sm font-medium text-gray-900 hover:text-blue-600">{room.name}</span>
                      {room.room_code && <span className="text-xs text-gray-500 ml-2">({room.room_code})</span>}
                      {room.public_visible === false && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                          Hidden from public
                        </span>
                      )}
                    </div>
                  </li>
                ))
              ) : (
                <li className="px-4 py-3 text-center text-gray-500">No rooms defined yet. Add one to get started!</li>
              )}
            </ul>
          </div>
        </div>
      </Card>

      {/* Add Room Modal (remains the same as previous step) */}
      <Modal isOpen={isAddModalOpen} onClose={closeAddModal} title="Add New Room">
        <form onSubmit={handleAddRoomSubmit}>
          <div className="mb-4">
            <label htmlFor="new-room-name" className="block text-sm font-medium text-gray-700 mb-1">Room Name *</label>
            <Input
              id="new-room-name"
              name="name"
              value={newRoomName}
              onChange={(e) => setNewRoomName(e.target.value)}
              placeholder="Enter room name (e.g., Cinema, K3)"
              required
              autoFocus
            />
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <Button type="button" variant="secondary" onClick={closeAddModal}>Cancel</Button>
            <Button type="submit" variant="primary">Add Room</Button>
          </div>
        </form>
      </Modal>

      {/* Edit Room Modal */}
      <Modal isOpen={isEditModalOpen} onClose={closeEditModal} title="Edit Room">
        <form onSubmit={handleSaveEditRoomSubmit}>
          <div className="mb-4">
            <label htmlFor="edit-room-name" className="block text-sm font-medium text-gray-700 mb-1">Room Name *</label>
            <Input
              id="edit-room-name"
              name="name" // Ensure name attribute is present for handleEditRoomInputChange
              value={roomToEdit.name}
              onChange={handleEditRoomInputChange}
              required
              autoFocus
            />
          </div>
          <div className="mb-4">
            <label htmlFor="edit-room-code-display" className="block text-sm font-medium text-gray-700 mb-1">Room Code (Permanent)</label>
            {/* Display as text or a disabled input */}
            <p
              id="edit-room-code-display"
              className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-100 text-gray-700 text-sm"
            >
              {roomToEdit.room_code || 'N/A'}
            </p>
            {/* Alternatively, a disabled input:
            <Input
              id="edit-room-code"
              name="room_code" // Still good to have a name even if disabled
              value={roomToEdit.room_code}
              disabled // Make it read-only
              className="bg-gray-100" // Style to indicate it's disabled
            /> 
            */}
          </div>
          <div className="mt-6 flex justify-between items-center pt-4 border-t">
            <Button
              type="button"
              variant="danger"
              onClick={() => handleDeleteRoom(roomToEdit.id, roomToEdit.name)}
              className="flex items-center space-x-1"
              title={`Delete ${roomToEdit.name}`}
            >
              <TrashIcon />
              <span>Delete Room</span>
            </Button>
            <div className="flex space-x-3">
              <Button type="button" variant="secondary" onClick={closeEditModal}>Cancel</Button>
              <Button type="submit" variant="primary">Save Changes</Button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default RoomsPage;